import { useEffect } from 'react';

/**
 * Auto-focuses an element (or a child matching selector) when isEditing becomes true.
 *
 * @param ref - Ref to the container element
 * @param isEditing - Whether edit mode is active
 * @param selector - Optional CSS selector to focus a child element (e.g. 'input')
 * @param useRAF - If true, delays focus until next animation frame (useful for portaled content)
 */
export function useFocusOnEdit(
  ref: React.RefObject<HTMLElement | null>,
  isEditing: boolean,
  selector?: string,
  useRAF: boolean = false
): void {
  useEffect(() => {
    if (!isEditing) return;

    const doFocus = () => {
      const elementToFocus = selector
        ? ref.current?.querySelector<HTMLElement>(selector)
        : ref.current;
      elementToFocus?.focus();
    };

    if (useRAF) {
      const id = requestAnimationFrame(doFocus);
      return () => cancelAnimationFrame(id);
    }
    doFocus();
  }, [ref, isEditing, selector, useRAF]);
}
